import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  BeakerIcon,
  PrinterIcon,
  DocumentDuplicateIcon,
} from "@heroicons/react/24/outline";

import { chemicalInventoryApi } from "../../../../../api/chemical-inventory";
import { ResourceLoading } from "../../../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../../../components/utils/ResourceError";
import { ResourceEmpty } from "../../../../../components/utils/ResourceEmpty";
import { HsePageChrome, useHseBusyAction } from "../../../../../components/dashboard/hse/HsePageChrome";
import { ModuleFilter } from "../../../../../components/utils/ModuleFilter";
import { ChemicalInventoryForm } from "../../../../../components/dashboard/quality/chemical-inventory-form";
import { ChemicalInventoryReviewBadge } from "../../../../../components/dashboard/quality/chemical-inventory-review-badge";
import { useUserHasAccess } from "../../../../../routes/roleOnly";
import { CHEMICAL_INVENTORY_WRITE_ROLES } from "../../../../../utils/chemicalInventoryAccess";
import { HseRowActionsMenu } from "../../../../../components/dashboard/hse/HseRowActionsMenu";
import { RiskSubnav } from "../../../../../components/dashboard/hse/risk/RiskSubnav";
import { HseListCard, HsePageBody } from "../../../../../components/dashboard/hse/HsePageBody";
import { HseGuideButton, HseModuleGuide } from "../../../../../components/dashboard/hse/HseModuleGuide";

/**
 * HSE/KS Chemical Inventory List - Stoffkartotek (ISO 14001, ISO 45001)
 */
export const HseKsChemicalInventoryList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canWrite = useUserHasAccess({ roles: CHEMICAL_INVENTORY_WRITE_ROLES });
  const [chemicals, setChemicals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filters, setFilters] = useState({
    search: "",
    hms_level: "",
    status: "",
    brand_id: "",
  });
  const { run, isBusy } = useHseBusyAction();
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 20,
    total: 0,
    total_pages: 0,
  });
  const [guideOpen, setGuideOpen] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedChemical, setSelectedChemical] = useState(null);

  const fetchChemicals = async (page = 1) => {
    try {
      setLoading(true);
      const params = {
        ...filters,
        page,
        per_page: pagination.per_page,
      };
      const response = await chemicalInventoryApi.getChemicalInventories(
        params
      );
      setError(false);
      setChemicals(response.data || []);
      setPagination({
        ...pagination,
        page,
        total: response.meta?.total || 0,
        total_pages: response.meta?.total_pages || 0,
      });
    } catch (err) {
      console.error("Error fetching chemical inventories:", err);
      setError(true);
      toast.error(t("chemical.inventory.load_error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChemicals();
  }, [filters]);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPagination({ ...pagination, page: 1 });
  };

  const handlePageChange = (page) => {
    fetchChemicals(page);
  };

  const handleExportPdf = (chemical) =>
    run(`pdf-${chemical.id}`, async () => {
      try {
        await chemicalInventoryApi.exportPdf(chemical.id, `stoffkartotek-${chemical.id}.pdf`);
        toast.success(t("chemical.inventory.pdf_ready"));
      } catch (err) {
        toast.error(err.message || t("chemical.inventory.pdf_failed"));
      }
    });

  const handleDeleteChemical = async (chemicalId) => {
    if (window.confirm(t("chemical.inventory.delete_confirm"))) {
      try {
        await chemicalInventoryApi.deleteChemicalInventory(chemicalId);
        toast.success(t("chemical.inventory.deleted"));
        fetchChemicals(pagination.page);
      } catch (err) {
        toast.error(t("chemical.inventory.delete_error"));
      }
    }
  };

  const getHmsLevelIcon = (level) => {
    switch (level) {
      case 1:
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 2:
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
      case 3:
        return <ExclamationTriangleIcon className="h-5 w-5 text-orange-500" />;
      case 4:
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <BeakerIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const baseClasses =
      "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    switch (status) {
      case "active":
        return (
          <span className={`${baseClasses} bg-green-100 text-green-800`}>
            {t("chemical.inventory.status.active")}
          </span>
        );
      case "inactive":
        return (
          <span className={`${baseClasses} bg-gray-100 text-gray-800`}>
            {t("chemical.inventory.status.inactive")}
          </span>
        );
      case "archived":
        return (
          <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>
            {t("chemical.inventory.status.archived")}
          </span>
        );
      default:
        return (
          <span className={`${baseClasses} bg-gray-100 text-gray-800`}>
            {status}
          </span>
        );
    }
  };

  const getHmsLevelBadge = (level) => {
    const baseClasses =
      "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    switch (level) {
      case 1:
        return (
          <span className={`${baseClasses} bg-green-100 text-green-800`}>
            {t("chemical.inventory.hms_level.1")}
          </span>
        );
      case 2:
        return (
          <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>
            {t("chemical.inventory.hms_level.2")}
          </span>
        );
      case 3:
        return (
          <span className={`${baseClasses} bg-orange-100 text-orange-800`}>
            {t("chemical.inventory.hms_level.3")}
          </span>
        );
      case 4:
        return (
          <span className={`${baseClasses} bg-red-100 text-red-800`}>
            {t("chemical.inventory.hms_level.4")}
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <HsePageChrome
        variant="list"
        title={t("chemical.inventory")}
        subtitle={t("chemical.inventory.list_subtitle")}
        listCount={loading ? null : pagination.total}
        mainButton={
          canWrite
            ? {
                label: t("chemical.inventory.new"),
                action: () => setShowCreateDialog(true),
                path: null,
                color: "",
                icon: PlusIcon,
              }
            : undefined
        }
      >
        <HsePageBody>
          <RiskSubnav />
          <div className="mb-4 flex justify-end">
            <HseGuideButton onClick={() => setGuideOpen(true)} />
          </div>
          <HseModuleGuide topic="chemical" open={guideOpen} onClose={() => setGuideOpen(false)} />

          <ModuleFilter
            filterCard
            hideStatusFilter
            searchPlaceholder={t("chemical.inventory.search")}
            initialFilters={{
              query: filters.search,
              chemical_statuses: filters.status ? [filters.status] : [],
              hms_levels: filters.hms_level ? [String(filters.hms_level)] : [],
            }}
            onFiltersChange={(next) =>
              handleFilterChange({
                ...filters,
                search: next.query || "",
                status: next.chemical_statuses?.[0] || "",
                hms_level: next.hms_levels?.[0] || "",
              })
            }
            customDropdownFilters={[
              {
                key: "chemical_statuses",
                label: t("chemical.inventory.filter_by_status"),
                options: ["active", "inactive", "archived"].map((status) => ({
                  value: status,
                  label: t(`chemical.inventory.status.${status}`),
                  class: "bg-slate-100 text-slate-800",
                })),
              },
              {
                key: "hms_levels",
                label: t("chemical.inventory.hms_level"),
                options: [1, 2, 3, 4].map((level) => ({
                  value: String(level),
                  label: t(`chemical.inventory.hms_level.${level}`),
                  class: "bg-slate-100 text-slate-800",
                })),
              },
            ]}
          />

        <HseListCard>
          {loading && chemicals.length === 0 ? (
            <div className="p-8">
              <ResourceLoading size="base" />
            </div>
          ) : error ? (
            <ResourceError errors={[t("chemical.inventory.load_error")]} onRetry={() => fetchChemicals()} />
          ) : !chemicals || chemicals.length === 0 ? (
            <div className="px-6 py-16">
              <ResourceEmpty message={t("chemical.inventory.no_results")} />
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {chemicals.map((chemical) => (
                <li
                  key={chemical.id}
                  className="cursor-pointer px-6 py-4 hover:bg-gray-50"
                  onClick={() =>
                    navigate(`/dashboard/hse-ks/risk/chemical-inventory/${chemical.id}`)
                  }
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <div className="flex-shrink-0">
                        {getHmsLevelIcon(chemical.hms_level)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center flex-wrap gap-2">
                          <Link
                            to={`/dashboard/hse-ks/risk/chemical-inventory/${chemical.id}`}
                            className="text-sm font-medium text-gray-900 truncate hover:text-[#1463C7]"
                            onClick={(event) => event.stopPropagation()}
                          >
                            {chemical.name}
                          </Link>
                          {getStatusBadge(chemical.status)}
                          {getHmsLevelBadge(chemical.hms_level)}
                          <ChemicalInventoryReviewBadge
                            reviewState={chemical.review_state}
                          />
                        </div>
                        <div className="flex items-center flex-wrap gap-x-4 text-sm text-gray-500">
                          <span>
                            {t("chemical.inventory.cas_number")}:{" "}
                            {chemical.cas_number || "—"}
                          </span>
                          <span>
                            {t("chemical.inventory.version")}:{" "}
                            {chemical.version ?? "—"}
                          </span>
                          <span>
                            {t("chemical.inventory.next_review_date")}:{" "}
                            {chemical.next_review_date
                              ? new Date(
                                  chemical.next_review_date
                                ).toLocaleDateString()
                              : "—"}
                          </span>
                          <span>{chemical.brand?.name || t("chemical.inventory.no_brand")}</span>
                        </div>
                      </div>
                    </div>
                    <div onClick={(event) => event.stopPropagation()}>
                      <HseRowActionsMenu
                        label={t("chemical.inventory.actions")}
                        items={[
                          {
                            label: t("chemical.inventory.open"),
                            icon: EyeIcon,
                            action: () =>
                              navigate(
                                `/dashboard/hse-ks/risk/chemical-inventory/${chemical.id}`
                              ),
                          },
                          {
                            label: t("chemical.inventory.edit"),
                            icon: PencilIcon,
                            hidden: !canWrite,
                            action: () => {
                              setSelectedChemical(chemical);
                              setShowCreateDialog(true);
                            },
                          },
                          {
                            label: t("chemical.inventory.pdf"),
                            icon: PrinterIcon,
                            disabled: isBusy(),
                            loading: isBusy(`pdf-${chemical.id}`),
                            action: () => handleExportPdf(chemical),
                          },
                          {
                            label: t("chemical.inventory.revisions"),
                            icon: DocumentDuplicateIcon,
                            action: () =>
                              navigate(
                                `/dashboard/hse-ks/risk/chemical-inventory/${chemical.id}#chemical-revisions`
                              ),
                          },
                          {
                            label: t("chemical.inventory.delete"),
                            icon: TrashIcon,
                            hidden: !canWrite,
                            action: () => handleDeleteChemical(chemical.id),
                          },
                        ]}
                      />
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </HseListCard>

        {pagination.total_pages > 1 && (
          <div className="mt-6 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              {t("chemical.inventory.showing")} {(pagination.page - 1) * pagination.per_page + 1} -{" "}
              {Math.min(
                pagination.page * pagination.per_page,
                pagination.total
              )}{" "}
              {t("chemical.inventory.of")} {pagination.total} {t("chemical.inventory.results")}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t("chemical.inventory.previous")}
              </button>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.total_pages}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t("chemical.inventory.next")}
              </button>
            </div>
          </div>
        )}
        </HsePageBody>
      </HsePageChrome>

      <ChemicalInventoryForm
        open={showCreateDialog}
        onClose={() => {
          setShowCreateDialog(false);
          setSelectedChemical(null);
        }}
        chemicalInventory={selectedChemical}
        onSuccess={() => {
          fetchChemicals(pagination.page);
          setShowCreateDialog(false);
          setSelectedChemical(null);
        }}
      />
    </>
  );
};
